import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Package, Image as ImageIcon, Inbox, Trash2, Plus, Loader2, ShoppingBag, FileText, Upload, LayoutDashboard, Truck, RefreshCcw } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { GARDIENS, gardienById } from "@/data/gardiens";
import { toast } from "sonner";

const inputCls = "w-full bg-muted/50 border border-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary/50 outline-none";
const emptyProduct = { slug: "", lookup_key: "", name: "", price: 0, category: "Mémoire", condition: "Alzheimer", description: "", image: "", gardien: "" };
const emptyPortfolio = { title: "", category: "", description: "", image: "", size: "normal" };
const SHIPPING = ["à préparer", "en préparation", "expédiée", "livrée"];
const shippingColor = { "à préparer": "bg-muted text-muted-foreground", "en préparation": "bg-warm/20 text-warm", "expédiée": "bg-primary/15 text-primary", "livrée": "bg-secondary/25 text-secondary-foreground" };

const CONTENT_FIELDS = [
  { section: "Accueil" },
  { key: "home_hero_eyebrow", label: "Petit titre (hero)", type: "text" },
  { key: "home_hero_title", label: "Grand titre (hero) — une ligne par saut de ligne", type: "textarea" },
  { key: "home_hero_subtitle", label: "Phrase défilante (bandeau sous l'image d'accueil)", type: "textarea" },
  { key: "home_hero_image", label: "Image de fond (hero)", type: "image" },
  { key: "home_manifesto_eyebrow", label: "Petit titre (manifeste)", type: "text" },
  { key: "home_manifesto_title", label: "Titre (manifeste)", type: "textarea" },
  { key: "home_manifesto_text", label: "Texte (manifeste)", type: "textarea" },
  { key: "home_manifesto_image", label: "Image (manifeste)", type: "image" },
  { section: "Boutique" },
  { key: "shop_eyebrow", label: "Petit titre", type: "text" },
  { key: "shop_title", label: "Grand titre", type: "textarea" },
  { key: "shop_subtitle", label: "Sous-titre", type: "textarea" },
  { key: "shop_offer_label", label: "Bandeau offre — texte en gras", type: "text" },
  { key: "shop_offer_text", label: "Bandeau offre — texte", type: "textarea" },
  { section: "Pathologies & Soins" },
  { key: "patho_eyebrow", label: "Petit titre", type: "text" },
  { key: "patho_title", label: "Grand titre", type: "textarea" },
  { key: "patho_subtitle", label: "Sous-titre", type: "textarea" },
  ...[1, 2, 3].flatMap((i) => [
    { key: `patho_${i}_name`, label: `Pathologie ${i} — Nom`, type: "text" },
    { key: `patho_${i}_intro`, label: `Pathologie ${i} — Introduction`, type: "textarea" },
    { key: `patho_${i}_image`, label: `Pathologie ${i} — Image`, type: "image" },
    ...[1, 2, 3].flatMap((j) => [
      { key: `patho_${i}_a${j}_t`, label: `Pathologie ${i} — Approche ${j} : titre`, type: "text" },
      { key: `patho_${i}_a${j}_d`, label: `Pathologie ${i} — Approche ${j} : texte`, type: "textarea" },
    ]),
  ]),
  { key: "benefits_title", label: "Titre de la section avantages", type: "textarea" },
  ...[1, 2, 3, 4, 5, 6].map((i) => ({ key: `benefit_${i}`, label: `Avantage ${i}`, type: "text" })),
  { key: "patho_cta_label", label: "Bouton final (vers la boutique)", type: "text" },
  { section: "Espace Jeux & Accompagnement" },
  { key: "jeux_eyebrow", label: "Petit titre", type: "text" },
  { key: "jeux_title", label: "Grand titre", type: "textarea" },
  { key: "jeux_subtitle", label: "Sous-titre", type: "textarea" },
  { key: "jeux_hero_quote", label: "Citation (carte de droite)", type: "textarea" },
  { key: "jeux_hero_image", label: "Image (carte de droite) — laissez vide pour afficher l'icône", type: "image" },
  { key: "jeux_offer_title", label: "Offre gratuite — titre", type: "textarea" },
  { key: "jeux_offer_text", label: "Offre gratuite — texte", type: "textarea" },
  ...[1, 2, 3].map((i) => ({ key: `jeux_offer_b${i}`, label: `Offre gratuite — point ${i}`, type: "text" })),
  { key: "jeux_features_title", label: "Titre de la section « Ce que propose l'application »", type: "textarea" },
  ...[1, 2, 3].flatMap((i) => [
    { key: `jeux_feature_${i}_t`, label: `Atout ${i} — titre`, type: "text" },
    { key: `jeux_feature_${i}_d`, label: `Atout ${i} — texte`, type: "textarea" },
  ]),
  { section: "Professionnels" },
  { key: "pro_eyebrow", label: "Petit titre", type: "text" },
  { key: "pro_title", label: "Grand titre", type: "textarea" },
  { key: "pro_subtitle", label: "Sous-titre", type: "textarea" },
  { key: "pro_price_title", label: "Tarifs — titre", type: "textarea" },
  { key: "pro_price_text", label: "Tarifs — texte", type: "textarea" },
  ...[1, 2, 3].flatMap((i) => [
    { key: `pro_price_${i}_t`, label: `Fourchette indicative ${i} — titre (vide = masquée)`, type: "text" },
    { key: `pro_price_${i}_d`, label: `Fourchette indicative ${i} — texte`, type: "textarea" },
  ]),
  { section: "Pied de page" },
  { key: "footer_description", label: "Description (sous le nom Passerelle)", type: "textarea" },
  { key: "footer_contact_text", label: "Texte de contact — une ligne par saut de ligne", type: "textarea" },
  { key: "footer_baseline", label: "Mention finale (après l'année ©)", type: "text" },
];

const AdminDashboard = () => {
  const { admin, setAdmin } = useApp();
  const nav = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [products, setProducts] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [requests, setRequests] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [content, setContent] = useState({});
  const [np, setNp] = useState(emptyProduct);
  const [nf, setNf] = useState(emptyPortfolio);
  const [busy, setBusy] = useState(false);
  const [savingKey, setSavingKey] = useState("");

  useEffect(() => {
    if (admin === false) nav("/admin/login");
  }, [admin, nav]);

  const loadAll = () => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/portfolio").then((r) => setPortfolio(r.data)).catch(() => {});
    api.get("/requests").then((r) => setRequests(r.data)).catch(() => {});
    api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/content").then((r) => setContent(r.data)).catch(() => {});
  };
  useEffect(() => { if (admin) loadAll(); }, [admin]);

  const logout = async () => { await api.post("/auth/logout"); setAdmin(false); nav("/admin/login"); };

  const saveContent = async (key) => {
    setSavingKey(key);
    try {
      await api.put("/content", { key, value: content[key] ?? "" });
      toast.success("Contenu mis à jour — visible sur le site après rafraîchissement");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setSavingKey("");
  };

  const uploadContentImage = async (key, file) => {
    if (!file) return;
    setSavingKey(key);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/content/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.put("/content", { key, value: data.url });
      setContent((c) => ({ ...c, [key]: data.url }));
      toast.success("Image mise à jour");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setSavingKey("");
  };

  const setShipping = async (sessionId, status) => {
    try {
      await api.patch(`/orders/${sessionId}/shipping`, { shipping_status: status });
      setOrders((prev) => prev.map((o) => o.session_id === sessionId ? { ...o, shipping_status: status } : o));
      toast.success("Statut d'expédition mis à jour");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const revokeAccess = async (sessionId) => {
    try {
      await api.post(`/orders/${sessionId}/revoke-access`);
      setOrders((prev) => prev.map((o) => o.session_id === sessionId ? { ...o, access_revoked: true, status: "refunded" } : o));
      toast.success("Accès à l'application révoqué");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const resendWelcome = async (sessionId) => {
    try {
      const { data } = await api.post(`/admin/orders/${sessionId}/resend-welcome`);
      toast.success(`Email d'accès renvoyé à ${data.resent_to}`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const addProduct = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/products", { ...np, price: parseFloat(np.price) });
      toast.success("Produit ajouté (synchronisé avec Stripe)");
      setNp(emptyProduct); loadAll();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setBusy(false);
  };
  const delProduct = async (slug) => { await api.delete(`/products/${slug}`); toast.success("Produit supprimé"); loadAll(); };

  const addPortfolio = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/portfolio", nf); toast.success("Réalisation ajoutée"); setNf(emptyPortfolio); loadAll(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setBusy(false);
  };
  const delPortfolio = async (id) => { await api.delete(`/portfolio/${id}`); toast.success("Réalisation supprimée"); loadAll(); };

  if (!admin) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  const tabs = [
    { id: "dashboard", label: "Tableau de bord", icon: LayoutDashboard },
    { id: "orders", label: "Commandes", icon: ShoppingBag },
    { id: "requests", label: "Demandes", icon: Inbox },
    { id: "content", label: "Contenu du site", icon: FileText },
    { id: "products", label: "Produits", icon: Package },
    { id: "portfolio", label: "Réalisations", icon: ImageIcon },
  ];

  return (
    <div className="min-h-screen flex bg-accent/20" data-testid="admin-dashboard">
      <aside className="w-64 bg-foreground text-background p-6 flex-col hidden md:flex">
        <div className="font-serif-display text-2xl">Atelier Sérénité</div>
        <p className="text-background/50 text-xs mt-1">Espace administration</p>
        <nav className="mt-10 space-y-1 flex-1">
          {tabs.map((t) => (
            <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-colors ${tab === t.id ? "bg-primary text-primary-foreground" : "hover:bg-background/10 text-background/70"}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </nav>
        <button onClick={logout} data-testid="admin-logout-btn" className="flex items-center gap-3 px-4 py-3 text-sm text-background/70 hover:text-background transition-colors">
          <LogOut className="w-4 h-4" /> Déconnexion
        </button>
      </aside>

      <main className="flex-1 p-6 lg:p-12 overflow-y-auto">
        <div className="md:hidden flex gap-2 mb-6">
          {tabs.map((t) => <button key={t.id} onClick={() => setTab(t.id)} className={`px-4 py-2 rounded-full text-sm ${tab === t.id ? "bg-primary text-primary-foreground" : "bg-card border border-border"}`}>{t.label}</button>)}
          <button onClick={logout} className="px-4 py-2 rounded-full text-sm bg-card border border-border">Sortir</button>
        </div>

        {tab === "dashboard" && (
          <section data-testid="dashboard-section">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
              <h1 className="font-serif-display text-4xl font-light">Tableau de bord</h1>
              <button onClick={loadAll} data-testid="stats-refresh-btn" className="rounded-full border border-border px-5 py-2 text-sm flex items-center gap-2 hover:bg-accent transition-colors">
                <RefreshCcw className="w-4 h-4" /> Actualiser
              </button>
            </div>
            {!stats ? (
              <p className="text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Chargement des statistiques…</p>
            ) : (
              <>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-card rounded-2xl border border-border/50 p-6" data-testid="stat-revenue-month">
                    <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">CA ce mois-ci</p>
                    <p className="font-serif-display text-4xl mt-3">{stats.revenue_month.toFixed(2)} €</p>
                  </div>
                  <div className="bg-card rounded-2xl border border-border/50 p-6" data-testid="stat-orders-month">
                    <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Commandes ce mois-ci</p>
                    <p className="font-serif-display text-4xl mt-3">{stats.orders_month}</p>
                  </div>
                  <div className="bg-card rounded-2xl border border-border/50 p-6" data-testid="stat-revenue-total">
                    <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">CA total</p>
                    <p className="font-serif-display text-4xl mt-3">{stats.revenue_total.toFixed(2)} €</p>
                  </div>
                  <div className="bg-card rounded-2xl border border-border/50 p-6" data-testid="stat-orders-total">
                    <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Commandes au total</p>
                    <p className="font-serif-display text-4xl mt-3">{stats.orders_total}</p>
                  </div>
                </div>
                <div className="grid sm:grid-cols-3 gap-4 mt-4">
                  <div className={`rounded-2xl border p-6 ${stats.to_ship > 0 ? "bg-warm/15 border-warm/40" : "bg-card border-border/50"}`} data-testid="stat-to-ship">
                    <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-2"><Truck className="w-4 h-4" /> À expédier</p>
                    <p className="font-serif-display text-2xl mt-3">{stats.to_ship}</p>
                    <p className="text-xs text-muted-foreground mt-1">à préparer ou en préparation</p>
                  </div>
                  <div className="bg-card rounded-2xl border border-border/50 p-6" data-testid="stat-provider-stripe">
                    <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Carte bancaire (Stripe)</p>
                    <p className="font-serif-display text-2xl mt-3">{stats.by_provider?.stripe?.revenue?.toFixed(2) ?? "0.00"} €</p>
                    <p className="text-xs text-muted-foreground mt-1">{stats.by_provider?.stripe?.orders ?? 0} commande(s)</p>
                  </div>
                  <div className="bg-card rounded-2xl border border-border/50 p-6" data-testid="stat-provider-paypal">
                    <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">PayPal</p>
                    <p className="font-serif-display text-2xl mt-3">{stats.by_provider?.paypal?.revenue?.toFixed(2) ?? "0.00"} €</p>
                    <p className="text-xs text-muted-foreground mt-1">{stats.by_provider?.paypal?.orders ?? 0} commande(s)</p>
                  </div>
                </div>
                {stats.refunded_total > 0 && (
                  <p className="text-sm text-muted-foreground mt-6" data-testid="stat-refunded">
                    {stats.refunded_total} commande(s) remboursée(s) — hors chiffre d'affaires.
                  </p>
                )}
              </>
            )}
          </section>
        )}

        {tab === "orders" && (
          <section data-testid="orders-section">
            <h1 className="font-serif-display text-4xl font-light mb-8">Commandes payées ({orders.length})</h1>
            <div className="space-y-4">
              {orders.length === 0 && <p className="text-muted-foreground">Aucune commande payée pour le moment.</p>}
              {orders.map((o) => (
                <div key={o.session_id} data-testid={`order-${o.session_id}`} className="bg-card rounded-2xl border border-border/50 p-6">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className={`text-xs px-3 py-1 rounded-full ${o.provider === "paypal" ? "bg-[#003087]/10 text-[#003087]" : "bg-primary/15 text-primary"}`}>{o.provider === "paypal" ? "PayPal" : "Stripe"}</span>
                      <span className="text-xs px-3 py-1 rounded-full bg-secondary/20 text-secondary-foreground" data-testid={`order-type-${o.session_id}`}>{o.customer_type === "professionnel" ? "Professionnel" : "Particulier"}</span>
                      {(o.amount || 0) >= 100 && o.customer_type !== "professionnel" && !o.access_revoked && (
                        <span className="text-xs px-3 py-1 rounded-full bg-warm/15 text-warm" data-testid={`order-access-${o.session_id}`}>Accès app offert</span>
                      )}
                      <span className="font-serif-display text-2xl">{(o.amount || 0).toFixed(2)} €</span>
                      <a href={`mailto:${o.customer_email}`} className="text-sm text-muted-foreground hover:text-primary">{o.customer_email || "—"}</a>
                    </div>
                    <span className="text-xs text-muted-foreground">{o.updated_at ? new Date(o.updated_at).toLocaleString("fr-FR") : ""}</span>
                  </div>
                  {o.items && o.items.length > 0 && (
                    <ul className="mt-4 text-sm text-foreground/70 space-y-1">
                      {o.items.map((it, idx) => (
                        <li key={idx} className="flex justify-between border-b border-border/40 pb-1">
                          <span>{it.name} <span className="text-muted-foreground">× {it.quantity}</span>{it.familiar && <span className="block text-xs text-warm mt-0.5">Familier : {it.familiar}</span>}</span>
                          <span>{(it.price * it.quantity).toFixed(2)} €</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="mt-5 flex items-center gap-3 flex-wrap">
                    <span className="text-sm text-foreground/70">Expédition :</span>
                    <span className={`text-xs px-3 py-1 rounded-full ${shippingColor[o.shipping_status] || "bg-muted text-muted-foreground"}`} data-testid={`order-status-${o.session_id}`}>{o.shipping_status || "à préparer"}</span>
                    <select
                      data-testid={`order-shipping-select-${o.session_id}`}
                      value={o.shipping_status || "à préparer"}
                      onChange={(e) => setShipping(o.session_id, e.target.value)}
                      className="ml-auto bg-muted/50 border border-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary/50 outline-none"
                    >
                      {SHIPPING.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="mt-3 flex items-center gap-3 flex-wrap">
                    <button
                      onClick={() => resendWelcome(o.session_id)}
                      data-testid={`order-resend-btn-${o.session_id}`}
                      className="text-xs rounded-full border border-primary/40 text-primary px-4 py-1.5 hover:bg-primary/10 transition-colors"
                    >
                      Renvoyer l'email d'accès
                    </button>
                    {o.access_revoked ? (
                      <span className="text-xs px-3 py-1 rounded-full bg-destructive/10 text-destructive" data-testid={`order-revoked-${o.session_id}`}>Accès révoqué</span>
                    ) : (
                      <button
                        onClick={() => revokeAccess(o.session_id)}
                        data-testid={`order-revoke-btn-${o.session_id}`}
                        className="text-xs rounded-full border border-destructive/40 text-destructive px-4 py-1.5 hover:bg-destructive/10 transition-colors"
                      >
                        Remboursement / révoquer l'accès
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {tab === "requests" && (
          <section>
            <h1 className="font-serif-display text-4xl font-light mb-8">Demandes reçues ({requests.length})</h1>
            <div className="space-y-4">
              {requests.length === 0 && <p className="text-muted-foreground">Aucune demande pour le moment.</p>}
              {requests.map((r) => (
                <div key={r.id} data-testid={`request-${r.id}`} className="bg-card rounded-2xl border border-border/50 p-6">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-3">
                      <span className={`text-xs px-3 py-1 rounded-full ${r.type === "custom_order" ? "bg-primary/15 text-primary" : r.type === "personalisation" ? "bg-warm/20 text-warm" : r.type === "pro_quote" ? "bg-[#3D8DB5]/15 text-[#3D8DB5]" : "bg-secondary/25 text-secondary-foreground"}`}>{r.type === "custom_order" ? "Sur mesure" : r.type === "personalisation" ? "Personnalisation" : r.type === "pro_quote" ? "Devis pro" : "Contact"}</span>
                      <span className="font-medium">{r.name}</span>
                      <a href={`mailto:${r.email}`} className="text-sm text-muted-foreground hover:text-primary">{r.email}</a>
                    </div>
                    <span className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString("fr-FR")}</span>
                  </div>
                  {r.type === "custom_order" ? (
                    <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm">
                      <p><b>Contexte :</b> {r.condition}</p>
                      <p><b>Téléphone :</b> {r.phone || "—"}</p>
                      <p className="sm:col-span-2"><b>Souvenirs :</b> {r.memories}</p>
                      <p className="sm:col-span-2"><b>Besoins :</b> {r.needs}</p>
                    </div>
                  ) : r.type === "pro_quote" ? (
                    <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm" data-testid={`pro-quote-request-${r.id}`}>
                      <p><b>Type :</b> {r.org_type}</p>
                      <p><b>Structure :</b> {r.org_name}</p>
                      <p><b>Téléphone :</b> {r.phone || "—"}</p>
                      <p><b>Résidents / bénéficiaires :</b> {r.residents_count || "—"}</p>
                      <p><b>Produits visés :</b> {r.products || "—"}</p>
                      <p><b>Quantités :</b> {r.quantities || "—"}</p>
                      {r.message && <p className="sm:col-span-2 text-foreground/70 italic">« {r.message} »</p>}
                    </div>
                  ) : r.type === "personalisation" ? (
                    <div className="mt-4 flex gap-5 flex-wrap" data-testid={`perso-request-${r.id}`}>
                      {r.photo_url && (
                        <a href={r.photo_url} target="_blank" rel="noopener noreferrer" className="shrink-0">
                          <img src={r.photo_url} alt="Photo de personnalisation" className="w-28 h-28 object-cover rounded-xl border border-border" data-testid={`perso-photo-${r.id}`} />
                        </a>
                      )}
                      <div className="flex-1 min-w-[220px] text-sm space-y-2">
                        <div className="flex flex-wrap gap-2">
                          {r.resident_first_name && <span className="text-xs rounded-full bg-muted px-3 py-1">Résident : {r.resident_first_name}</span>}
                          {r.theme && <span className="text-xs rounded-full bg-secondary/25 text-secondary-foreground px-3 py-1" data-testid={`perso-theme-${r.id}`}>{r.theme}</span>}
                          {r.technique && <span className="text-xs rounded-full bg-primary/15 text-primary px-3 py-1" data-testid={`perso-technique-${r.id}`}>Technique : {r.technique}</span>}
                        </div>
                        {r.product && <p><b>Produit :</b> {r.product}</p>}
                        <p className="text-foreground/70 italic">« {r.message} »</p>
                      </div>
                    </div>
                  ) : <p className="mt-4 text-sm text-foreground/70">{r.message}</p>}
                </div>
              ))}
            </div>
          </section>
        )}

        {tab === "content" && (
          <section data-testid="content-section">
            <h1 className="font-serif-display text-4xl font-light mb-3">Contenu du site</h1>
            <p className="text-sm text-muted-foreground mb-8">Modifiez les textes et images. Les changements apparaissent sur le site après rafraîchissement de la page.</p>
            <div className="space-y-6 max-w-3xl">
              {CONTENT_FIELDS.map((f, idx) => (
                f.section ? (
                  <h2 key={`section-${idx}`} data-testid={`content-section-${f.section.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="font-serif-display text-2xl pt-6 border-b border-border/50 pb-3">{f.section}</h2>
                ) : (
                <div key={f.key} className="bg-card rounded-2xl border border-border/50 p-6" data-testid={`content-field-${f.key}`}>
                  <label className="text-sm font-medium block mb-3">{f.label}</label>
                  {f.type === "image" ? (
                    <div className="flex items-center gap-5 flex-wrap">
                      {content[f.key] && <img src={content[f.key]} alt="" className="w-32 h-24 object-cover rounded-xl border border-border" />}
                      <div className="flex-1 min-w-[200px]">
                        <input value={content[f.key] || ""} onChange={(e) => setContent({ ...content, [f.key]: e.target.value })} className={inputCls} placeholder="URL de l'image" data-testid={`content-input-${f.key}`} />
                        <div className="flex gap-2 mt-2">
                          <label className="cursor-pointer rounded-full border border-border px-4 py-2 text-sm hover:bg-accent transition-colors flex items-center gap-2">
                            <Upload className="w-4 h-4" /> Téléverser
                            <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid={`content-upload-${f.key}`} onChange={(e) => uploadContentImage(f.key, e.target.files?.[0])} />
                          </label>
                          <button onClick={() => saveContent(f.key)} disabled={savingKey === f.key} className="rounded-full bg-primary text-primary-foreground px-5 py-2 text-sm flex items-center gap-2 disabled:opacity-60" data-testid={`content-save-${f.key}`}>
                            {savingKey === f.key ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Enregistrer l'URL
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-3">
                      {f.type === "textarea" ? (
                        <textarea rows={3} value={content[f.key] || ""} onChange={(e) => setContent({ ...content, [f.key]: e.target.value })} className={inputCls} data-testid={`content-input-${f.key}`} />
                      ) : (
                        <input value={content[f.key] || ""} onChange={(e) => setContent({ ...content, [f.key]: e.target.value })} className={inputCls} data-testid={`content-input-${f.key}`} />
                      )}
                      <button onClick={() => saveContent(f.key)} disabled={savingKey === f.key} className="self-start rounded-full bg-primary text-primary-foreground px-6 py-2.5 text-sm flex items-center gap-2 disabled:opacity-60" data-testid={`content-save-${f.key}`}>
                        {savingKey === f.key ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Enregistrer
                      </button>
                    </div>
                  )}
                </div>
                )
              ))}
            </div>
          </section>
        )}

        {tab === "products" && (
          <section>
            <h1 className="font-serif-display text-4xl font-light mb-8">Produits ({products.length})</h1>
            <form onSubmit={addProduct} className="bg-card rounded-2xl border border-border/50 p-6 mb-8 grid sm:grid-cols-2 gap-4" data-testid="add-product-form">
              <input required placeholder="Slug (ex: mon-objet)" className={inputCls} value={np.slug} onChange={(e) => setNp({ ...np, slug: e.target.value })} data-testid="np-slug" />
              <input required placeholder="Lookup key Stripe (ex: mon_objet)" className={inputCls} value={np.lookup_key} onChange={(e) => setNp({ ...np, lookup_key: e.target.value })} data-testid="np-lookup" />
              <input required placeholder="Nom du produit" className={inputCls} value={np.name} onChange={(e) => setNp({ ...np, name: e.target.value })} data-testid="np-name" />
              <input required type="number" step="0.01" placeholder="Prix €" className={inputCls} value={np.price} onChange={(e) => setNp({ ...np, price: e.target.value })} data-testid="np-price" />
              <select className={inputCls} value={np.category} onChange={(e) => setNp({ ...np, category: e.target.value })}><option>Mémoire</option><option>Apaisement</option><option>Stimulation</option><option>Repères</option></select>
              <select className={inputCls} value={np.condition} onChange={(e) => setNp({ ...np, condition: e.target.value })}><option>Alzheimer</option><option>Autisme</option><option>Particuliers</option></select>
              <input required placeholder="URL image" className={inputCls} value={np.image} onChange={(e) => setNp({ ...np, image: e.target.value })} data-testid="np-image" />
              <input required placeholder="Description" className={inputCls} value={np.description} onChange={(e) => setNp({ ...np, description: e.target.value })} data-testid="np-desc" />
              <select className={inputCls} value={np.gardien} onChange={(e) => setNp({ ...np, gardien: e.target.value })} data-testid="np-gardien">
                <option value="">Sans Gardien fétiche</option>
                {GARDIENS.map((g) => <option key={g.id} value={g.id}>{g.name} — {g.title}</option>)}
              </select>
              <button disabled={busy} className="sm:col-span-2 rounded-full bg-primary text-primary-foreground py-3 flex items-center justify-center gap-2" data-testid="add-product-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Ajouter le produit</button>
            </form>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {products.map((p) => (
                <div key={p.slug} className="bg-card rounded-2xl border border-border/50 overflow-hidden" data-testid={`admin-product-${p.slug}`}>
                  <img src={p.image} alt={p.name} className="w-full h-32 object-cover" />
                  <div className="p-4">
                    <p className="font-medium text-sm">{p.name}</p>
                    <p className="text-xs text-muted-foreground mt-1">{p.price} € · {p.category}</p>
                    <button onClick={() => delProduct(p.slug)} className="mt-3 text-xs text-destructive flex items-center gap-1 hover:underline" data-testid={`del-product-${p.slug}`}><Trash2 className="w-3 h-3" /> Supprimer</button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {tab === "portfolio" && (
          <section>
            <h1 className="font-serif-display text-4xl font-light mb-8">Réalisations ({portfolio.length})</h1>
            <form onSubmit={addPortfolio} className="bg-card rounded-2xl border border-border/50 p-6 mb-8 grid sm:grid-cols-2 gap-4" data-testid="add-portfolio-form">
              <input required placeholder="Titre" className={inputCls} value={nf.title} onChange={(e) => setNf({ ...nf, title: e.target.value })} data-testid="nf-title" />
              <input required placeholder="Catégorie" className={inputCls} value={nf.category} onChange={(e) => setNf({ ...nf, category: e.target.value })} data-testid="nf-category" />
              <input required placeholder="URL image" className={inputCls} value={nf.image} onChange={(e) => setNf({ ...nf, image: e.target.value })} data-testid="nf-image" />
              <input required placeholder="Description" className={inputCls} value={nf.description} onChange={(e) => setNf({ ...nf, description: e.target.value })} data-testid="nf-desc" />
              <button disabled={busy} className="sm:col-span-2 rounded-full bg-primary text-primary-foreground py-3 flex items-center justify-center gap-2" data-testid="add-portfolio-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Ajouter la réalisation</button>
            </form>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {portfolio.map((p) => (
                <div key={p.id} className="bg-card rounded-2xl border border-border/50 overflow-hidden" data-testid={`admin-portfolio-${p.id}`}>
                  <img src={p.image} alt={p.title} className="w-full h-32 object-cover" />
                  <div className="p-4">
                    <p className="font-medium text-sm">{p.title}</p>
                    <p className="text-xs text-muted-foreground mt-1">{p.category}</p>
                    <button onClick={() => delPortfolio(p.id)} className="mt-3 text-xs text-destructive flex items-center gap-1 hover:underline" data-testid={`del-portfolio-${p.id}`}><Trash2 className="w-3 h-3" /> Supprimer</button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
